import type { DepositAccount } from '@fruitful/income-split';
import { format } from 'date-fns';
import { useAtomValue } from 'jotai';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { TabPanel, TabView } from 'primereact/tabview';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    BodyMediumRegular,
    BodySmallRegular,
    CheckIcon,
    ChevronDownIcon,
    ChevronUpIcon,
    ClockIcon,
    H2Regular,
    H6Emphasized,
    Icon,
    Input,
    LabelRegular,
    SmoothieScrollView,
    Spinner,
    XIcon,
    XStack,
    YStack,
} from 'smoothie';
import { colorTokens } from 'smoothie/src/themes/SmoothieColorTokens';

import useGetAccountsForMembership from '../../banking/banking/hooks/useGetAccountsForMembership';
import { ConfirmModal } from '../../common/components/ConfirmModal';
import { Button } from '../../common/components/GroveButtons';
import { GroveSimpleModal } from '../../common/components/GroveSimpleModal';
import { centsToFormattedDollars } from '../../common/utils/formatting';
import {
    syncteraAccountUrl,
    syncteraPostedTransactionUrl,
} from '../../common/utils/LinkUtils';
import { adminUserAtom } from '../../entities/entityAtoms';
import { requestBuilder } from '../../http/RequestBuilder';
import {
    type AdminBankOpsManualReferralRewardPayoutRequestDto,
    type AdminBankOpsManualReferralRewardPayoutResponseDto,
    type ReferralContext,
    type ReferralDataTables,
    type ReferralPartyPayoutDto,
    ReferralPartyPayoutMethod,
    ReferralProgram,
    type ReferralSummaryResponseDto,
    type ReferralTrackingDisplayDto,
} from '../types/ReferralsTypes';

const isCoreNickname = (nickname: string): boolean =>
    nickname.trim().toLowerCase().includes('core');

/** Core-named accounts first, then joint accounts, so default `[0]` matches dropdown order. */
const sortInternalDepositAccountsForManualPayout = (
    accounts: DepositAccount[],
): DepositAccount[] =>
    [...accounts].sort((a, b) => {
        const aCore = isCoreNickname(a.nickname);
        const bCore = isCoreNickname(b.nickname);

        if (aCore !== bCore) {
            return aCore ? -1 : 1;
        }
        if (a.isJoint !== b.isJoint) {
            return a.isJoint ? -1 : 1;
        }
        // After grouping by core/joint, sort alphabetically by nickname
        return a.nickname.localeCompare(b.nickname);
    });

const useGetReferralSummary = requestBuilder.query<
    ReferralSummaryResponseDto,
    Record<string, never>
>({
    endpoint: () => '/api/admin/bank-ops/referrals/summary',
    queryKey: () => '/api/admin/bank-ops/referrals/summary',
});

export const BankOpsReferralsPanel = () => {
    const { data: referralSummary, isLoading: isLoadingReferralSummary } =
        useGetReferralSummary({});

    if (isLoadingReferralSummary) {
        return <Spinner />;
    }

    if (referralSummary == null) {
        return (
            <BodyMediumRegular>No referral data available</BodyMediumRegular>
        );
    }

    return (
        <TabView
            style={{ width: '100%' }}
            panelContainerStyle={{
                width: '100%',
                padding: 0,
                marginTop: 32,
            }}
        >
            <TabPanel header="Legacy">
                <ReferralProgramPanel
                    data={referralSummary[ReferralProgram.LEGACY]}
                    program={ReferralProgram.LEGACY}
                />
            </TabPanel>
            <TabPanel header="Premium Trial">
                <ReferralProgramPanel
                    data={referralSummary[ReferralProgram.PREMIUM_TRIAL]}
                    program={ReferralProgram.PREMIUM_TRIAL}
                />
            </TabPanel>
        </TabView>
    );
};

const ReferralProgramPanel = ({
    data,
    program,
}: {
    data: ReferralDataTables;
    program: ReferralProgram;
}) => (
    <YStack gap={16}>
        <CollapsibleReferralSection
            title="1. Ready to be Paid"
            count={data.readyToBePaid.length}
            defaultExpanded
        >
            <ReferralSummaryTable data={data.readyToBePaid} program={program} />
        </CollapsibleReferralSection>

        <CollapsibleReferralSection
            title="2. Pending"
            count={data.pending.length}
            defaultExpanded
        >
            <ReferralSummaryTable data={data.pending} program={program} />
        </CollapsibleReferralSection>

        <CollapsibleReferralSection
            title="3. Processed"
            count={data.processed.length}
            defaultExpanded
        >
            <ReferralTrackingTable data={data.processed} />
        </CollapsibleReferralSection>

        <CollapsibleReferralSection
            title="4. Expired"
            count={data.expired.length}
            defaultExpanded={false}
        >
            <ReferralSummaryTable data={data.expired} program={program} />
        </CollapsibleReferralSection>
    </YStack>
);

const CollapsibleReferralSection = ({
    title,
    count,
    defaultExpanded,
    children,
}: {
    title: string;
    count: number;
    defaultExpanded: boolean;
    children: React.ReactNode;
}) => {
    const [isExpanded, setIsExpanded] = useState(defaultExpanded);

    return (
        <YStack gap={8}>
            <XStack
                alignItems="center"
                alignSelf="flex-start"
                gap={8}
                hoverStyle={{ cursor: 'pointer' }}
                onPress={() => {
                    setIsExpanded((open) => !open);
                }}
            >
                <Icon
                    icon={isExpanded ? ChevronUpIcon : ChevronDownIcon}
                    size={16}
                    color="$gray1100"
                />
                <H6Emphasized>
                    {title} ({count})
                </H6Emphasized>
            </XStack>
            {isExpanded ? children : null}
        </YStack>
    );
};

const ReferralSummaryTable = ({
    data,
    program,
}: {
    data: ReferralContext[];
    program: ReferralProgram;
}) => {
    const [selectedReferral, setSelectedReferral] =
        useState<ReferralContext | null>();

    if (data.length === 0) {
        return <BodyMediumRegular>No referrals</BodyMediumRegular>;
    }

    return (
        <>
            <DataTable
                dataKey="referral_id"
                value={data}
                size="small"
                tableStyle={{ width: '100%' }}
                selectionMode="single"
                selection={selectedReferral}
                onSelectionChange={(e) => {
                    setSelectedReferral(
                        e.value as ReferralContext | null | undefined,
                    );
                }}
                isDataSelectable={(_) => true}
            >
                <Column
                    header="Row"
                    style={{ width: '4rem', verticalAlign: 'top' }}
                    body={(_, options) => {
                        return <>{options.rowIndex + 1}</>;
                    }}
                />
                <Column
                    field="referral_id"
                    header="Referral"
                    style={{
                        width: '6rem',
                        verticalAlign: 'top',
                    }}
                    body={(x: ReferralContext) => (
                        <YStack
                            backgroundColor={
                                x.decision_logged_in_grove
                                    ? colorTokens.light.red.red1000
                                    : 'unset'
                            }
                        >
                            <BodySmallRegular style={{ fontSize: '0.75rem' }}>
                                Referral ID: {x.referral_id}
                                <br />
                                Membership ID: {x.membership_id}
                                <br />
                                Created:{' '}
                                {typeof x.referral_created_at === 'string'
                                    ? x.referral_created_at
                                    : format(
                                          x.referral_created_at,
                                          'yyyy-MM-dd HH:mm',
                                      )}
                                <br />
                                Ready to Pay: {x.ready_to_pay ? 'Yes' : 'No'}
                                <br />
                            </BodySmallRegular>
                        </YStack>
                    )}
                />
                <Column
                    header="Referrer"
                    style={{ width: '6rem', verticalAlign: 'top' }}
                    body={(x: ReferralContext) => (
                        <BodySmallRegular style={{ fontSize: '0.75rem' }}>
                            Name: {x.referrer_name}
                            <br />
                            User ID: {x.referrer_id}
                            <br />
                            {x.referrer_bank_account_id != null && (
                                <>
                                    Bank Account ID:{' '}
                                    {x.referrer_bank_account_id}
                                    <br />
                                </>
                            )}
                            {x.referrer_synctera_account_id != null && (
                                <>
                                    Synctera Account:{' '}
                                    <a
                                        href={syncteraAccountUrl(
                                            x.referrer_synctera_account_id,
                                        )}
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        {x.referrer_synctera_account_id}
                                    </a>
                                    <br />
                                </>
                            )}
                        </BodySmallRegular>
                    )}
                />
                <Column
                    header="Referee"
                    style={{ width: '6rem', verticalAlign: 'top' }}
                    body={(x: ReferralContext) => {
                        const refereeNames = [
                            x.referee_primary_name,
                            x.referee_partner_name,
                        ]
                            .filter((name): name is string => name != null)
                            .join(', ');
                        return (
                            <BodySmallRegular style={{ fontSize: '0.75rem' }}>
                                {refereeNames.length > 0 ? (
                                    <>Name(s): {refereeNames}</>
                                ) : (
                                    <>Name(s): N/A</>
                                )}
                                <br />
                                Membership ID: {x.membership_id}
                                <br />
                                {x.referee_primary_user_id != null && (
                                    <>
                                        Primary User ID:{' '}
                                        {x.referee_primary_user_id}
                                        <br />
                                    </>
                                )}
                                {x.referee_partner_user_id != null && (
                                    <>
                                        Partner User ID:{' '}
                                        {x.referee_partner_user_id}
                                        <br />
                                    </>
                                )}
                                {x.referee_bank_account_id != null && (
                                    <>
                                        Bank Account ID:{' '}
                                        {x.referee_bank_account_id}
                                        <br />
                                    </>
                                )}
                                {x.referee_synctera_account_id != null && (
                                    <>
                                        Synctera Account:{' '}
                                        <a
                                            href={syncteraAccountUrl(
                                                x.referee_synctera_account_id,
                                            )}
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            {x.referee_synctera_account_id}
                                        </a>
                                        <br />
                                    </>
                                )}
                            </BodySmallRegular>
                        );
                    }}
                />
                <Column
                    header="Qualification Status"
                    style={{ width: '6rem', verticalAlign: 'top' }}
                    body={(x: ReferralContext) =>
                        program === ReferralProgram.LEGACY ? (
                            <LegacyQualificationStatus referral={x} />
                        ) : (
                            <PremiumTrialQualificationStatus referral={x} />
                        )
                    }
                />
            </DataTable>
            <GroveSimpleModal
                isOpen={selectedReferral != null}
                onClose={() => {
                    setSelectedReferral(null);
                }}
            >
                <SmoothieScrollView maxHeight="80vh" width="70vw">
                    {selectedReferral != null && (
                        <ManualReferralRewardButtonAndPopup
                            key={selectedReferral.referral_id}
                            referral={selectedReferral}
                        />
                    )}
                </SmoothieScrollView>
            </GroveSimpleModal>
        </>
    );
};

const QualificationRow = ({
    isQualified,
    isFailed = false,
    children,
}: {
    isQualified: boolean;
    isFailed?: boolean;
    children: React.ReactNode;
}) => {
    const icon = isQualified ? CheckIcon : isFailed ? XIcon : ClockIcon;
    const color = isQualified
        ? '$green1000'
        : isFailed
          ? '$red1000'
          : '$orange1000';

    return (
        <XStack gap={4} alignItems="center">
            <Icon icon={icon} size={12} color={color} />
            {children}
        </XStack>
    );
};

const LegacyQualificationStatus = ({
    referral,
}: {
    referral: ReferralContext;
}) => {
    const daysSinceSignupQualified =
        (referral.referee_days_since_signup ?? 0) >= 45;
    const stripeStatusQualified =
        referral.referee_stripe_status?.toLowerCase() === 'active';
    const discoveryQualified =
        referral.referee_guidance_session_discovery_status.toLowerCase() ===
        'completed';
    const moneyMapQualified =
        referral.referee_guidance_session_money_map_status.toLowerCase() ===
        'completed';
    const optimizationQualified =
        referral.referee_guidance_session_optimization_status?.toLowerCase() ===
        'completed';

    return (
        <YStack>
            <BodySmallRegular style={{ fontSize: '0.75rem' }}>
                <QualificationRow isQualified={daysSinceSignupQualified}>
                    Days Since Signup:{' '}
                    {referral.referee_days_since_signup == null
                        ? 'N/A'
                        : Math.round(referral.referee_days_since_signup)}
                </QualificationRow>
                <QualificationRow isQualified={stripeStatusQualified}>
                    Stripe Status: {referral.referee_stripe_status}
                </QualificationRow>
                Guidance Sessions:
                <QualificationRow isQualified={discoveryQualified}>
                    &nbsp;&nbsp;Discovery:{' '}
                    {referral.referee_guidance_session_discovery_status}
                </QualificationRow>
                <QualificationRow isQualified={moneyMapQualified}>
                    &nbsp;&nbsp;Money Map:{' '}
                    {referral.referee_guidance_session_money_map_status}
                </QualificationRow>
                <QualificationRow isQualified={optimizationQualified}>
                    &nbsp;&nbsp;Optimization:{' '}
                    {referral.referee_guidance_session_optimization_status}
                </QualificationRow>
            </BodySmallRegular>
        </YStack>
    );
};

const PremiumTrialQualificationStatus = ({
    referral,
}: {
    referral: ReferralContext;
}) => {
    const discoveryQualified =
        referral.referee_guidance_session_discovery_status.toLowerCase() ===
        'completed';
    const moneyMapQualified =
        referral.referee_guidance_session_money_map_status.toLowerCase() ===
        'completed';
    const stripeStatusQualified = ['active', 'trialing'].includes(
        referral.referee_stripe_status?.toLowerCase() ?? '',
    );
    const firstCashOpenedAt =
        referral.referee_first_cash_opened_at == null
            ? 'N/A'
            : format(
                  new Date(referral.referee_first_cash_opened_at),
                  'yyyy-MM-dd HH:mm',
              );

    return (
        <YStack>
            <BodySmallRegular style={{ fontSize: '0.75rem' }}>
                <QualificationRow isQualified={referral.referrer_has_open_cash}>
                    Referrer Cash account
                </QualificationRow>
                <QualificationRow isQualified={stripeStatusQualified}>
                    Stripe status: {referral.referee_stripe_status ?? 'N/A'}
                </QualificationRow>
                <QualificationRow isQualified={discoveryQualified}>
                    Kickoff:{' '}
                    {referral.referee_guidance_session_discovery_status}
                </QualificationRow>
                <QualificationRow isQualified={moneyMapQualified}>
                    Optimize:{' '}
                    {referral.referee_guidance_session_money_map_status}
                </QualificationRow>
                <QualificationRow isQualified={firstCashOpenedAt !== 'N/A'}>
                    Cash account opened
                </QualificationRow>
                <QualificationRow
                    isQualified={referral.referee_money_map_includes_open_cash}
                >
                    Active Money Map
                </QualificationRow>
                <QualificationRow
                    isQualified={referral.referee_has_qualifying_direct_deposit}
                    isFailed={
                        referral.expired &&
                        !referral.referee_has_qualifying_direct_deposit
                    }
                >
                    Direct deposit received (first 60 days):{' '}
                    {referral.referee_qualifying_dd_cents == null
                        ? 'None'
                        : centsToFormattedDollars(
                              referral.referee_qualifying_dd_cents,
                          )}
                </QualificationRow>
            </BodySmallRegular>
        </YStack>
    );
};

const useManualReferralRewardPayout = requestBuilder.mutation<
    AdminBankOpsManualReferralRewardPayoutResponseDto,
    AdminBankOpsManualReferralRewardPayoutRequestDto
>({
    endpoint: '/api/admin/bank-ops/referrals/payout-manual',
    method: 'POST',
});

const manualPayoutSelectStyle: React.CSSProperties = {
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#ccc',
    fontSize: 16,
    width: '100%',
};

interface ReferralPartyManualPayoutSectionProps {
    partyHeading: string;
    method: ReferralPartyPayoutMethod;
    onMethodChange: (method: ReferralPartyPayoutMethod) => void;
    bankAccountId: string | null;
    onBankAccountIdChange: (id: string | null) => void;
    internalAccounts: DepositAccount[];
    isLoadingAccounts: boolean;
}

const ReferralPartyManualPayoutSection = ({
    partyHeading,
    method,
    onMethodChange,
    bankAccountId,
    onBankAccountIdChange,
    internalAccounts,
    isLoadingAccounts,
}: ReferralPartyManualPayoutSectionProps) => (
    <YStack gap={8}>
        <H6Emphasized>{partyHeading}</H6Emphasized>
        <LabelRegular>Payout method</LabelRegular>
        <select
            value={method}
            onChange={(e) => {
                onMethodChange(e.target.value as ReferralPartyPayoutMethod);
            }}
            style={manualPayoutSelectStyle}
        >
            <option value={ReferralPartyPayoutMethod.STRIPE_CREDIT}>
                Stripe credit
            </option>
            <option value={ReferralPartyPayoutMethod.SYNCTERA_REWARD}>
                Synctera reward (bank account)
            </option>
            <option value={ReferralPartyPayoutMethod.DO_NOT_PAY}>
                Do not pay
            </option>
        </select>
        {method === ReferralPartyPayoutMethod.SYNCTERA_REWARD ? (
            <>
                <LabelRegular>Bank account</LabelRegular>
                {isLoadingAccounts ? (
                    <Spinner />
                ) : (
                    <select
                        value={bankAccountId ?? ''}
                        onChange={(e) => {
                            onBankAccountIdChange(e.target.value || null);
                        }}
                        style={manualPayoutSelectStyle}
                    >
                        <option value="">Select bank account…</option>
                        {internalAccounts.map((account) => (
                            <option key={account.id} value={account.id}>
                                {account.nickname} (*
                                {account.accountNumberMasked}){' '}
                                {account.isJoint ? '[joint]' : ''}{' '}
                                {account.availableBalance != null
                                    ? ` - ${centsToFormattedDollars(account.availableBalance)}`
                                    : ''}
                            </option>
                        ))}
                    </select>
                )}
                {bankAccountId != null && (
                    <BodySmallRegular>
                        <a
                            href={syncteraAccountUrl(bankAccountId)}
                            target="_blank"
                            rel="noreferrer"
                        >
                            View Synctera account
                        </a>
                    </BodySmallRegular>
                )}
            </>
        ) : null}
    </YStack>
);

const describePayoutChoice = (
    method: ReferralPartyPayoutMethod,
    bankAccountName?: string,
): string => {
    switch (method) {
        case ReferralPartyPayoutMethod.STRIPE_CREDIT:
            return 'Stripe credit';
        case ReferralPartyPayoutMethod.SYNCTERA_REWARD:
            return `Synctera reward (${bankAccountName})`;
        case ReferralPartyPayoutMethod.DO_NOT_PAY:
            return 'do not pay';
        default: {
            const _exhaustive: never = method;
            return _exhaustive;
        }
    }
};

const ManualReferralRewardButtonAndPopup = ({
    referral,
}: {
    referral: ReferralContext;
}) => {
    const adminUser = useAtomValue(adminUserAtom);
    const [referrerMethod, setReferrerMethod] = useState(
        ReferralPartyPayoutMethod.SYNCTERA_REWARD,
    );
    const [refereeMethod, setRefereeMethod] = useState(
        ReferralPartyPayoutMethod.SYNCTERA_REWARD,
    );

    const [note, setNote] = useState(
        `${adminUser.email}: manual referral payout`,
    );
    const [selectedReferrerBankAccountId, setSelectedReferrerBankAccountId] =
        useState<string | null>(referral.referrer_bank_account_id ?? null);
    const [selectedRefereeBankAccountId, setSelectedRefereeBankAccountId] =
        useState<string | null>(referral.referee_bank_account_id ?? null);

    const [manualPayoutButtonDisabled, setManualPayoutButtonDisabled] =
        useState<boolean>(false);
    const [isManualPayoutConfirmModalOpen, setIsManualPayoutConfirmModalOpen] =
        useState(false);
    const [manualPayoutResult, setManualPayoutResult] =
        useState<AdminBankOpsManualReferralRewardPayoutResponseDto | null>();

    // Fetch accounts for referrer and referee memberships
    const { data: referrerAccounts, isLoading: isLoadingReferrerAccounts } =
        useGetAccountsForMembership(
            { membershipId: referral.referrer_membership_id },
            { enabled: referral.referrer_membership_id != null },
        );

    const { data: refereeAccounts, isLoading: isLoadingRefereeAccounts } =
        useGetAccountsForMembership(
            { membershipId: referral.membership_id },
            { enabled: referral.membership_id != null },
        );

    // Filter to internal accounts, then sort (joint first) so defaults and dropdown agree
    const internalReferrerAccounts = useMemo(() => {
        const internal =
            referrerAccounts?.filter((account) => !account.isExternal) ?? [];
        return sortInternalDepositAccountsForManualPayout(internal);
    }, [referrerAccounts]);

    const internalRefereeAccounts = useMemo(() => {
        const internal =
            refereeAccounts?.filter((account) => !account.isExternal) ?? [];
        return sortInternalDepositAccountsForManualPayout(internal);
    }, [refereeAccounts]);

    const referrerPayoutDefaultAppliedRef = useRef(false);
    const refereePayoutDefaultAppliedRef = useRef(false);

    useEffect(() => {
        if (isLoadingReferrerAccounts) {
            return;
        }
        if (referrerPayoutDefaultAppliedRef.current) {
            return;
        }
        referrerPayoutDefaultAppliedRef.current = true;
        if (internalReferrerAccounts.length === 0) {
            setReferrerMethod(ReferralPartyPayoutMethod.STRIPE_CREDIT);
            return;
        }
        const fromContext = referral.referrer_bank_account_id;
        const contextIdValid =
            fromContext != null &&
            fromContext !== '' &&
            internalReferrerAccounts.some((a) => a.id === fromContext);
        if (!contextIdValid && internalReferrerAccounts[0] != null) {
            setSelectedReferrerBankAccountId(internalReferrerAccounts[0].id);
        }
    }, [
        isLoadingReferrerAccounts,
        internalReferrerAccounts,
        referral.referrer_bank_account_id,
    ]);

    useEffect(() => {
        if (isLoadingRefereeAccounts) {
            return;
        }
        if (refereePayoutDefaultAppliedRef.current) {
            return;
        }
        refereePayoutDefaultAppliedRef.current = true;
        if (internalRefereeAccounts.length === 0) {
            setRefereeMethod(ReferralPartyPayoutMethod.STRIPE_CREDIT);
            return;
        }
        const fromContext = referral.referee_bank_account_id;
        const contextIdValid =
            fromContext != null &&
            fromContext !== '' &&
            internalRefereeAccounts.some((a) => a.id === fromContext);
        if (!contextIdValid && internalRefereeAccounts[0] != null) {
            setSelectedRefereeBankAccountId(internalRefereeAccounts[0].id);
        }
    }, [
        isLoadingRefereeAccounts,
        internalRefereeAccounts,
        referral.referee_bank_account_id,
    ]);

    const { mutateAsync: manualReferralRewardPayout } =
        useManualReferralRewardPayout();

    const buildPartyPayout = (
        method: ReferralPartyPayoutMethod,
        bankAccountId: string | null,
    ): ReferralPartyPayoutDto =>
        method === ReferralPartyPayoutMethod.SYNCTERA_REWARD
            ? {
                  method,
                  bank_account_id: bankAccountId ?? undefined,
              }
            : { method };

    const syncteraNeedsAccount = (method: ReferralPartyPayoutMethod) =>
        method === ReferralPartyPayoutMethod.SYNCTERA_REWARD;

    const isManualPayoutSubmitBlocked =
        (syncteraNeedsAccount(referrerMethod) &&
            (selectedReferrerBankAccountId == null ||
                selectedReferrerBankAccountId === '')) ||
        (syncteraNeedsAccount(refereeMethod) &&
            (selectedRefereeBankAccountId == null ||
                selectedRefereeBankAccountId === ''));

    const handleManualPayoutSubmit = async () => {
        setManualPayoutButtonDisabled(true);
        setIsManualPayoutConfirmModalOpen(false);

        const updatedContext: ReferralContext = {
            ...referral,
            referrer_bank_account_id: syncteraNeedsAccount(referrerMethod)
                ? selectedReferrerBankAccountId
                : null,
            referee_bank_account_id: syncteraNeedsAccount(refereeMethod)
                ? selectedRefereeBankAccountId
                : null,
        };

        const referrer_payout = buildPartyPayout(
            referrerMethod,
            selectedReferrerBankAccountId,
        );
        const referee_payout = buildPartyPayout(
            refereeMethod,
            selectedRefereeBankAccountId,
        );

        const manualPayoutResult = await manualReferralRewardPayout({
            body: {
                context: updatedContext,
                referrer_payout,
                referee_payout,
                note,
            },
        });
        setManualPayoutResult(manualPayoutResult);
        referral.decision_logged_in_grove = true;
    };

    const confirmDescription = `Referrer: ${describePayoutChoice(
        referrerMethod,
        internalReferrerAccounts.find(
            (a) => a.id === selectedReferrerBankAccountId,
        )?.nickname ?? '',
    )}\nReferee: ${describePayoutChoice(
        refereeMethod,
        internalRefereeAccounts.find(
            (a) => a.id === selectedRefereeBankAccountId,
        )?.nickname ?? '',
    )}\n\nApply these payout choices?`;

    return (
        <>
            <YStack marginTop={32}>
                <YStack gap={8} marginBottom={16}>
                    <H2Regular>Manual referral payout</H2Regular>
                </YStack>
                <LabelRegular marginLeft={16}>Reward note:</LabelRegular>
                <Input
                    value={note}
                    onChangeText={setNote}
                    placeholder="Enter note"
                />

                <BodySmallRegular marginVertical={16}>
                    This should only be triggered by Fruitful Bank Ops.
                </BodySmallRegular>
                <YStack gap={24}>
                    <ReferralPartyManualPayoutSection
                        partyHeading={`Referrer (${referral.referrer_name})`}
                        method={referrerMethod}
                        onMethodChange={setReferrerMethod}
                        bankAccountId={selectedReferrerBankAccountId}
                        onBankAccountIdChange={setSelectedReferrerBankAccountId}
                        internalAccounts={internalReferrerAccounts}
                        isLoadingAccounts={isLoadingReferrerAccounts}
                    />
                    <ReferralPartyManualPayoutSection
                        partyHeading={`Referee (${referral.referee_primary_name ?? referral.membership_id})`}
                        method={refereeMethod}
                        onMethodChange={setRefereeMethod}
                        bankAccountId={selectedRefereeBankAccountId}
                        onBankAccountIdChange={setSelectedRefereeBankAccountId}
                        internalAccounts={internalRefereeAccounts}
                        isLoadingAccounts={isLoadingRefereeAccounts}
                    />
                </YStack>
                <br />
                <br />

                <XStack justifyContent="center" gap={10}>
                    <Button
                        onPress={(_) => {
                            setIsManualPayoutConfirmModalOpen(true);
                        }}
                        disabled={
                            manualPayoutButtonDisabled ||
                            isManualPayoutSubmitBlocked
                        }
                        variant="primary"
                        tone="positive"
                        width="auto"
                        size="tiny"
                    >
                        Submit manual payout
                    </Button>
                </XStack>
                <BodyMediumRegular></BodyMediumRegular>
                {manualPayoutResult != null && (
                    <>
                        <BodyMediumRegular>COMPLETE</BodyMediumRegular>
                        <BodyMediumRegular>
                            {JSON.stringify(manualPayoutResult, null, 2)}
                        </BodyMediumRegular>
                    </>
                )}
            </YStack>

            <ConfirmModal
                isOpen={isManualPayoutConfirmModalOpen}
                onClose={() => {
                    setIsManualPayoutConfirmModalOpen(false);
                    setManualPayoutResult(null);
                }}
                onConfirm={handleManualPayoutSubmit}
                description={confirmDescription}
            />
        </>
    );
};

const ReferralTrackingTable = ({
    data,
}: {
    data: ReferralTrackingDisplayDto[];
}) => {
    if (data.length === 0) {
        return <BodyMediumRegular>No paid referrals</BodyMediumRegular>;
    }

    return (
        <DataTable
            dataKey="id"
            value={data}
            size="small"
            tableStyle={{ width: '100%' }}
        >
            <Column
                field="id"
                header="Referral"
                style={{ width: '4rem', verticalAlign: 'top' }}
                body={(x: ReferralTrackingDisplayDto) => (
                    <BodySmallRegular style={{ fontSize: '0.75rem' }}>
                        ID: {x.id}
                        <br />
                        Membership ID: {x.membership_id}
                        <br />
                        Created:{' '}
                        {format(new Date(x.created_at), 'yyyy-MM-dd HH:mm')}
                        {x.updated_at != null && (
                            <>
                                <br />
                                Updated:{' '}
                                {format(
                                    new Date(x.updated_at),
                                    'yyyy-MM-dd HH:mm',
                                )}
                            </>
                        )}
                    </BodySmallRegular>
                )}
            />
            <Column
                header="Referrer"
                style={{ width: '6rem', verticalAlign: 'top' }}
                body={(x: ReferralTrackingDisplayDto) => (
                    <BodySmallRegular style={{ fontSize: '0.75rem' }}>
                        Name: {x.referrer_name}
                        <br />
                        User ID: {x.referrer_id}
                        <br />
                        Payout method: {x.referrer_payout_method ?? 'N/A'}
                        <br />
                        {x.referrer_bank_account_id != null && (
                            <>
                                Bank Account ID: {x.referrer_bank_account_id}
                                <br />
                            </>
                        )}
                        {x.referrer_reward_amount_cents != null && (
                            <>
                                Reward Amount:{' '}
                                {centsToFormattedDollars(
                                    x.referrer_reward_amount_cents,
                                )}
                                <br />
                            </>
                        )}
                        {x.referrer_synctera_reward_id != null && (
                            <>
                                Synctera Reward ID:{' '}
                                {x.referrer_synctera_reward_id}
                                <br />
                            </>
                        )}
                        {x.referrer_stripe_balance_transaction_id != null && (
                            <>
                                Stripe balance transaction ID:{' '}
                                {x.referrer_stripe_balance_transaction_id}
                                <br />
                            </>
                        )}
                        Reward Transaction:{' '}
                        {x.referrer_reward_transaction_uuid == null ? (
                            <>no reward issued</>
                        ) : (
                            <a
                                href={syncteraPostedTransactionUrl(
                                    x.referrer_reward_transaction_uuid,
                                )}
                                target="_blank"
                                rel="noreferrer"
                            >
                                {x.referrer_reward_transaction_uuid}
                            </a>
                        )}
                    </BodySmallRegular>
                )}
            />
            <Column
                header="Referee"
                style={{ width: '6rem', verticalAlign: 'top' }}
                body={(x: ReferralTrackingDisplayDto) => (
                    <BodySmallRegular style={{ fontSize: '0.75rem' }}>
                        Payout method: {x.referee_payout_method ?? 'N/A'}
                        <br />
                        {x.referee_bank_account_id != null && (
                            <>
                                Bank Account ID: {x.referee_bank_account_id}
                                <br />
                            </>
                        )}
                        {x.referee_reward_amount_cents != null && (
                            <>
                                Reward Amount:{' '}
                                {centsToFormattedDollars(
                                    x.referee_reward_amount_cents,
                                )}
                                <br />
                            </>
                        )}
                        {x.referee_synctera_reward_id != null && (
                            <>
                                Synctera Reward ID:{' '}
                                {x.referee_synctera_reward_id}
                                <br />
                            </>
                        )}
                        {x.referee_stripe_balance_transaction_id != null && (
                            <>
                                Stripe balance transaction ID:{' '}
                                {x.referee_stripe_balance_transaction_id}
                                <br />
                            </>
                        )}
                        Reward Transaction:{' '}
                        {x.referee_reward_transaction_uuid == null ? (
                            <>no reward issued</>
                        ) : (
                            <a
                                href={syncteraPostedTransactionUrl(
                                    x.referee_reward_transaction_uuid,
                                )}
                                target="_blank"
                                rel="noreferrer"
                            >
                                {x.referee_reward_transaction_uuid}
                            </a>
                        )}
                    </BodySmallRegular>
                )}
            />
            <Column field="note" header="Note" />
        </DataTable>
    );
};
